import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import { GATEWAY_URL } from '../../../Config/Constants';
import { Storage } from '../../../Utils/StorageUtils';
import {
  AIAgentChatRequest,
  AIAgentChatResponse,
  AIAgentDiagnostics,
  AIAgentDialogue,
  AIAgentMetrics,
  AIAgentSessionResponse,
} from '../AIAgentTypes';
import { mapConfigurationError } from '../AIAgentErrorMapper';
import { clearStoredConversationState } from './AIAgentConversationStorage';

const AUTH_TOKEN_KEY = 'api-token-key';
export const AI_AGENT_CONVERSATION_STORAGE_KEY = 'sgd-ai-agent-conversation-id';
const AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY = 'sgd-ai-agent-conversation-owner';
const REQUEST_TIMEOUT_MS = 60000;
const MAX_REPLY_LENGTH = 65536;
const MAX_MESSAGE_LENGTH = 4000;

export class AIAgentValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AIAgentValidationError';
  }
}

export class AIAgentConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AIAgentConfigurationError';
  }
}

export class AIAgentMalformedResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AIAgentMalformedResponseError';
  }
}

export class AIAgentUnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AIAgentUnauthorizedError';
  }
}

export class AIAgentSessionInvalidatedError extends Error {
  constructor() {
    super('A inicialização da sessão não pertence mais ao usuário ou conversa atual.');
    this.name = 'AIAgentSessionInvalidatedError';
  }
}

interface SessionRequest {
  generation: number;
  controller: AbortController;
  promise: Promise<string>;
}

let dedicatedClient: AxiosInstance | null = null;
let agentBaseUrl: string | null = null;
let sessionInFlight: SessionRequest | null = null;
let sessionGeneration = 0;

export function deriveAgentBaseUrl(gatewayUrl: string | undefined): string {
  if (!gatewayUrl || !String(gatewayUrl).trim()) {
    throw new AIAgentConfigurationError(mapConfigurationError().message);
  }

  let parsed: URL;
  try {
    parsed = new URL(String(gatewayUrl).trim());
  } catch {
    throw new AIAgentConfigurationError(mapConfigurationError().message);
  }

  parsed.pathname = '/agent';
  parsed.search = '';
  parsed.hash = '';

  return parsed.toString().replace(/\/$/, '');
}

function readAuthToken(): string | null {
  const token =
    Storage.local.get(AUTH_TOKEN_KEY) || Storage.session.get(AUTH_TOKEN_KEY);
  if (typeof token !== 'string' || !token.trim()) {
    return null;
  }
  return token.trim();
}

function stripIdentityHeaders(headers: AxiosRequestHeaders | undefined): void {
  if (!headers) {
    return;
  }

  const identityKeys = [
    'Email',
    'email',
    'X-User-Email',
    'x-user-email',
    'X-USER-EMAIL',
  ];

  const mutableHeaders = headers as unknown as Record<string, unknown> & {
    delete?: (name: string) => void;
  };

  identityKeys.forEach((key) => {
    if (typeof mutableHeaders.delete === 'function') {
      mutableHeaders.delete(key);
    } else {
      delete mutableHeaders[key];
    }
  });
}

function getClient(): AxiosInstance {
  if (!dedicatedClient || !agentBaseUrl) {
    agentBaseUrl = deriveAgentBaseUrl(GATEWAY_URL);
    dedicatedClient = axios.create({
      baseURL: agentBaseUrl,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    dedicatedClient.interceptors.request.use((config) => {
      const token = readAuthToken();
      if (!token) {
        throw new AIAgentUnauthorizedError(
          'Token de autenticação ausente para o assistente.'
        );
      }

      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
      stripIdentityHeaders(config.headers as AxiosRequestHeaders);
      return config;
    });
  }

  return dedicatedClient;
}

export function resetAIAgentServiceForTests(): void {
  sessionInFlight?.controller.abort();
  dedicatedClient = null;
  agentBaseUrl = null;
  sessionInFlight = null;
  sessionGeneration = 0;
}

export function getDedicatedClientForTests(): AxiosInstance | null {
  return dedicatedClient;
}

export function getAgentBaseUrlForTests(): string | null {
  return agentBaseUrl;
}

function readCurrentSubject(): string | null {
  const token = readAuthToken();
  if (!token) {
    return null;
  }

  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
    const subject = JSON.parse(decoded)?.sub;
    return typeof subject === 'string' && subject.trim() ? subject.trim() : null;
  } catch {
    return null;
  }
}

function removeConversationStorage(): void {
  clearStoredConversationState();

  try {
    window.sessionStorage.removeItem(AI_AGENT_CONVERSATION_STORAGE_KEY);
    window.sessionStorage.removeItem(AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }

  try {
    window.localStorage.removeItem(AI_AGENT_CONVERSATION_STORAGE_KEY);
    window.localStorage.removeItem(AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}

export function getStoredConversationId(): string | null {
  try {
    const value = window.sessionStorage.getItem(AI_AGENT_CONVERSATION_STORAGE_KEY);
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    const currentSubject = readCurrentSubject();
    const storedSubject = window.sessionStorage.getItem(
      AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY
    );
    if (
      (currentSubject && storedSubject !== currentSubject) ||
      (!currentSubject && storedSubject)
    ) {
      removeConversationStorage();
      return null;
    }

    return value.trim();
  } catch {
    return null;
  }
}

export function clearStoredConversationId(): void {
  sessionGeneration += 1;
  sessionInFlight?.controller.abort();
  sessionInFlight = null;
  removeConversationStorage();
}

function persistConversationId(conversationId: string): void {
  window.sessionStorage.setItem(AI_AGENT_CONVERSATION_STORAGE_KEY, conversationId);
  const currentSubject = readCurrentSubject();
  if (currentSubject) {
    window.sessionStorage.setItem(
      AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY,
      currentSubject
    );
  } else {
    window.sessionStorage.removeItem(AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY);
  }
  try {
    window.localStorage.removeItem(AI_AGENT_CONVERSATION_STORAGE_KEY);
    window.localStorage.removeItem(AI_AGENT_CONVERSATION_OWNER_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function mapNonNegativeNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return undefined;
  }
  return value;
}

function mapMetrics(raw: unknown): AIAgentMetrics | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const source = raw as Record<string, unknown>;
  const metrics: AIAgentMetrics = { ...source };

  const iterations = mapNonNegativeNumber(source.iterations);
  const selfHealingRetries = mapNonNegativeNumber(source.selfHealingRetries);
  const agentLatencyMs = mapNonNegativeNumber(source.agentLatencyMs);
  const totalLatencyMs = mapNonNegativeNumber(source.totalLatencyMs);

  if (iterations !== undefined) metrics.iterations = iterations;
  else delete metrics.iterations;

  if (selfHealingRetries !== undefined) {
    metrics.selfHealingRetries = selfHealingRetries;
  } else {
    delete metrics.selfHealingRetries;
  }

  if (agentLatencyMs !== undefined) metrics.agentLatencyMs = agentLatencyMs;
  else delete metrics.agentLatencyMs;

  if (totalLatencyMs !== undefined) metrics.totalLatencyMs = totalLatencyMs;
  else delete metrics.totalLatencyMs;

  if (typeof source.disambiguated === 'boolean') {
    metrics.disambiguated = source.disambiguated;
  } else {
    delete metrics.disambiguated;
  }

  return metrics;
}

function mapDialogue(raw: unknown): AIAgentDialogue | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const source = raw as Record<string, unknown>;
  const dialogue: AIAgentDialogue = {};

  if (typeof source.outcome === 'string' && source.outcome.trim()) {
    dialogue.outcome = source.outcome.trim().slice(0, 128);
  }
  if (typeof source.intent === 'string' && source.intent.trim()) {
    dialogue.intent = source.intent.trim().slice(0, 128);
  }

  return Object.keys(dialogue).length > 0 ? dialogue : undefined;
}

function buildDiagnostics(data: AIAgentChatResponse): AIAgentDiagnostics | undefined {
  const metrics = mapMetrics(data.metrics);
  const dialogue = mapDialogue(data.dialogue);
  const traceId =
    typeof data.traceId === 'string' && data.traceId.trim()
      ? data.traceId.trim().slice(0, 256)
      : undefined;

  if (!traceId && !metrics && !dialogue) {
    return undefined;
  }

  return {
    traceId,
    metrics,
    dialogue,
  };
}

function assertValidReply(reply: unknown): string {
  if (typeof reply !== 'string' || !reply.trim()) {
    throw new AIAgentMalformedResponseError('Resposta inválida do agente.');
  }

  if (reply.length > MAX_REPLY_LENGTH) {
    throw new AIAgentMalformedResponseError('Resposta inválida do agente.');
  }

  return reply;
}

export async function createSession(signal?: AbortSignal): Promise<string> {
  const generation = sessionGeneration;
  const subject = readCurrentSubject();
  const client = getClient();
  const response = await client.post<AIAgentSessionResponse>(
    '/session',
    undefined,
    { signal }
  );

  const conversationId = response.data?.conversationId;
  if (typeof conversationId !== 'string' || !conversationId.trim()) {
    throw new AIAgentMalformedResponseError('Sessão inválida do agente.');
  }

  const normalized = conversationId.trim();
  if (
    signal?.aborted ||
    generation !== sessionGeneration ||
    subject !== readCurrentSubject()
  ) {
    throw new AIAgentSessionInvalidatedError();
  }
  persistConversationId(normalized);
  return normalized;
}

function waitForSessionRequest(
  promise: Promise<string>,
  signal?: AbortSignal
): Promise<string> {
  if (!signal) {
    return promise;
  }
  if (signal.aborted) {
    return Promise.reject(new AIAgentSessionInvalidatedError());
  }

  return new Promise<string>((resolve, reject) => {
    const abort = () => reject(new AIAgentSessionInvalidatedError());
    signal.addEventListener('abort', abort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', abort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', abort);
        reject(error);
      }
    );
  });
}

export async function ensureSession(signal?: AbortSignal): Promise<string> {
  const existing = getStoredConversationId();
  if (existing) {
    return existing;
  }

  if (!sessionInFlight) {
    const controller = new AbortController();
    let request: SessionRequest;
    const promise = createSession(controller.signal).finally(() => {
      if (sessionInFlight === request) {
        sessionInFlight = null;
      }
    });
    request = {
      generation: sessionGeneration,
      controller,
      promise,
    };
    sessionInFlight = request;
  }

  return waitForSessionRequest(sessionInFlight.promise, signal);
}

export async function replaceSession(signal?: AbortSignal): Promise<string> {
  clearStoredConversationId();
  return ensureSession(signal);
}

export async function sendMessage(
  message: string,
  conversationId: string,
  signal?: AbortSignal
): Promise<{ reply: string; diagnostics?: AIAgentDiagnostics }> {
  const normalizedMessage = message.trim();
  if (!normalizedMessage) {
    throw new AIAgentValidationError('Mensagem vazia.');
  }

  if (normalizedMessage.length > MAX_MESSAGE_LENGTH) {
    throw new AIAgentValidationError('Mensagem inválida.');
  }

  if (!conversationId || !conversationId.trim()) {
    throw new AIAgentValidationError('Sessão ausente.');
  }

  const client = getClient();
  const body: AIAgentChatRequest = { message: normalizedMessage };

  const response = await client.post<AIAgentChatResponse>('/chat', body, {
    headers: {
      'X-Conversation-Id': conversationId.trim(),
    },
    signal,
  });

  const reply = assertValidReply(response.data?.reply);

  return {
    reply,
    diagnostics: buildDiagnostics(response.data ?? { reply }),
  };
}
